import React, { useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';

// Lazy load components for better performance
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const VendorDashboard = lazy(() => import('./components/VendorDashboard'));
const AddProduct = lazy(() => import('./components/AddProduct'));
const EditProduct = lazy(() => import('./components/EditProduct'));
const PublicWebsite = lazy(() => import('./components/PublicWebsite'));
const StaticPage = lazy(() => import('./components/StaticPage'));
const Login = lazy(() => import('./components/pages/Login/Login'));
const VendorRegistration = lazy(() => import('./components/VendorRegistration'));
const VendorDetailPage = lazy(() => import('./components/VendorDetailPage'));
const CustomerDashboard = lazy(() => import('./components/CustomerDashboard'));
// const CustomerRegistration = lazy(() => import('./components/CustomerRegistration'));
const Registration = lazy(() => import('./components/pages/Register/Registration'));
const CheckoutNew = lazy(() => import('./components/CheckoutNew'));
const PaymentCallback = lazy(() => import('./components/PaymentCallback'));
const VendorVerification = lazy(() => import('./components/VendorVerification'));
const Payments = lazy(() => import('./components/admin/Payments'));
const Testimonials = lazy(() => import('./components/pages/Testimonial/Testimonial'));

//Front end

const AboutPage = lazy(() => import('./components/pages/AboutUs/about'));
const SearchPage = lazy(() => import('./components/pages/Products/Search'));
const ContactPage = lazy(() => import('./components/pages/ContactUs/contact'));
const ProductDetail = lazy(() => import('./components/pages/Products/ProductDetail'));
const HomeWithHeader = lazy(() => import('./components/HomeWithHeader'));

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();


  // Wait for auth to finish initializing to avoid redirecting on refresh
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('Not authenticated, redirecting to login');
    // Redirect to login with the required role
    return <Navigate to={`/login/${requiredRole}?redirect=${location.pathname}`} replace />;
  }

  if (user?.role !== requiredRole) {
    console.log('Role mismatch, redirecting to correct dashboard');
    // Redirect to appropriate dashboard based on user's actual role
    if (user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (user?.role === 'vendor') {
      return <Navigate to="/vendor" replace />;
    } else if (user?.role === 'customer') {
      return <Navigate to="/customer" replace />;

    }
    return <Navigate to="/" replace />;
  }

  console.log('Access granted, rendering children');
  return children;
};

// Login Component with URL parameters
const LoginPage = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const role = searchParams.get('role') || 'admin';
  const redirect = searchParams.get('redirect') || (role === 'admin' ? '/admin' : '/vendor');

  return (
    <Login
      onBackToWebsite={() => window.location.href = '/'}
      defaultRole={role}
      redirectPath={redirect}
    />
  );
};

// Admin Login Page
const AdminLoginPage = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirect = searchParams.get('redirect') || '/admin';

  return (
    <Login
      defaultRole="admin"
      redirectPath={redirect}
      showRoleSelection={false}
    />
  );
};

// Vendor Login Page
const VendorLoginPage = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirect = searchParams.get('redirect') || '/vendor';

  return (
    <Login
      defaultRole="vendor"
      redirectPath={redirect}
      showRoleSelection={false}
    />
  );
};

// Customer Login Page
const CustomerLoginPage = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirect = searchParams.get('redirect') || '/customer';

  return (
    <Login
      defaultRole="customer"
      redirectPath={redirect}
      showRoleSelection={false}
    />
  );
};

// Main App Router
const AppRouter = () => {

  return (
    <Routes>

      <Route path="/" element={<HomeWithHeader />} />
      <Route path="/product/:id" element={<ProductDetail />} />
      <Route path="/category/:categoryId" element={<PublicWebsite />} />
      <Route path="/brand/:slug" element={<PublicWebsite />} />
      <Route path="/search" element={<PublicWebsite />} />
      <Route path="/checkout" element={<CheckoutNew />} />
      <Route path="/order-confirmation" element={<PublicWebsite />} />
      <Route path="/payment/callback" element={<PaymentCallback />} />
      <Route path="/payment/success" element={<PaymentCallback />} />
      <Route path="/payment/failed" element={<PaymentCallback />} />
      <Route path="/page/:slug" element={<StaticPage />} />

      {/* Front end */}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/testimonials" element={<Testimonials />} />
      <Route path="/products" element={<SearchPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/home" element={<HomeWithHeader />} />

      {/* Login Pages */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/admin" element={<AdminLoginPage />} />
      <Route path="/login/vendor" element={<VendorLoginPage />} />
      <Route path="/login/customer" element={<CustomerLoginPage />} />

      {/* Registration Pages */}
      <Route path="/register" element={<Registration />} />
      <Route path="/register/vendor" element={
        <VendorRegistration onBackToLogin={() => window.location.href = '/login/vendor'} />
      } />
      <Route path="/vendor/verify" element={<VendorVerification />} />

      {/* <Route path="/register/customer" element={
        <CustomerRegistration onBackToLogin={() => window.location.href = '/login/customer'} />
      } /> */}

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Customer Routes */}
      <Route
        path="/customer"
        element={
          <ProtectedRoute requiredRole="customer">
            <CustomerDashboard />
          </ProtectedRoute>
        }
      />

      {/* Vendor Routes */}
      <Route
        path="/vendor"
        element={
          <ProtectedRoute requiredRole="vendor">
            <VendorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor/add-product"
        element={
          <ProtectedRoute requiredRole="vendor">
            <AddProduct />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor/edit-product/:id"
        element={
          <ProtectedRoute requiredRole="vendor">
            <EditProduct />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/add-product"
        element={
          <ProtectedRoute requiredRole="admin">
            <AddProduct />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/edit-product/:id"
        element={
          <ProtectedRoute requiredRole="admin">
            <EditProduct />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/vendors/:id"
        element={
          <ProtectedRoute requiredRole="admin">
            <VendorDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <ProtectedRoute requiredRole="admin">
            <Payments />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Router>
          <div className="App">
            <AppRouter />
          </div>
        </Router>
      </CartProvider>
    </AuthProvider>
  );
}
export default App;